import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getPaymentInfo, createPaymentOrder, verifyPayment } from '../services/api'
import OsteoLogo from '../components/Logo'

const CASE_ID_REGEX = /^[A-Z0-9][A-Z0-9_-]{2,49}$/

export default function PaymentPage() {
  const { caseId: paramCaseId } = useParams()
  const navigate = useNavigate()

  // Step 1: Case ID entry (skipped if URL has caseId)
  const [input, setInput] = useState(paramCaseId || '')
  const [caseId, setCaseId] = useState(paramCaseId || null)
  const inputRef = useRef(null)

  // Step 2: Payment info
  const [info, setInfo] = useState(null) // { amount, description, status }
  const [loading, setLoading] = useState(!!paramCaseId)
  const [error, setError] = useState(null)

  // Step 3: Payer details
  const [payerName, setPayerName] = useState('')
  const [payerEmail, setPayerEmail] = useState('')
  const [payerPhone, setPayerPhone] = useState('')

  // Step 4: Payment state
  const [paying, setPaying] = useState(false)
  const [success, setSuccess] = useState(false)
  const [paymentId, setPaymentId] = useState(null)

  // Load payment info when caseId is set
  useEffect(() => {
    if (!caseId) return
    setLoading(true)
    setError(null)
    getPaymentInfo(caseId)
      .then((res) => setInfo(res.data))
      .catch((err) => {
        setError(err.response?.data?.error || 'Unable to fetch payment information.')
        setCaseId(null)
      })
      .finally(() => setLoading(false))
  }, [caseId])

  function handleChange(e) {
    setInput(e.target.value.toUpperCase().replace(/\s/g, ''))
    setError(null)
  }

  function handleLookup(e) {
    e.preventDefault()
    const trimmed = input.trim()
    if (!trimmed) {
      setError('Please enter your Case ID.')
      inputRef.current?.focus()
      return
    }
    if (!CASE_ID_REGEX.test(trimmed)) {
      setError('Invalid format. Check your Case ID and try again.')
      inputRef.current?.focus()
      return
    }
    setCaseId(trimmed)
    navigate(`/pay/${trimmed}`, { replace: true })
  }

  async function handlePay() {
    if (!payerName.trim() || payerName.trim().length < 2) {
      setError('Please enter your full name (at least 2 characters).')
      return
    }
    if (!payerEmail.trim() && !payerPhone.trim()) {
      setError('Please enter your email or phone number for the payment receipt.')
      return
    }
    if (payerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerEmail.trim())) {
      setError('Please enter a valid email address.')
      return
    }
    if (payerPhone.trim()) {
      const phoneDigits = payerPhone.trim().replace(/\D/g, '')
      if (phoneDigits.length < 7 || phoneDigits.length > 15) {
        setError('Please enter a valid phone number (7–15 digits).')
        return
      }
    }

    setError(null)
    setPaying(true)

    try {
      // 1. Create Razorpay order
      const orderRes = await createPaymentOrder(caseId, {
        payerName: payerName.trim(),
        payerEmail: payerEmail.trim() || undefined,
        payerPhone: payerPhone.trim() || undefined,
      })

      const { orderId, amount, currency, keyId } = orderRes.data

      // 2. Open Razorpay checkout
      const options = {
        key: keyId,
        amount,
        currency,
        order_id: orderId,
        name: 'Osteo3D',
        description: `Payment for Case ${caseId}`,
        prefill: {
          name: payerName.trim(),
          email: payerEmail.trim() || undefined,
          contact: payerPhone.trim() || undefined,
        },
        theme: { color: '#0d9488' },
        handler: async function (response) {
          // 3. Verify payment on backend
          try {
            const verifyRes = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            })
            setSuccess(true)
            setPaymentId(verifyRes.data.paymentId || response.razorpay_payment_id)
          } catch (err) {
            setError(err.response?.data?.error || 'Payment verification failed. Please contact support.')
          } finally {
            setPaying(false)
          }
        },
        modal: {
          ondismiss: function () {
            setPaying(false)
          },
        },
      }

      const rzp = new window.Razorpay(options)
      rzp.on('payment.failed', function (response) {
        setError(response.error?.description || 'Payment failed. Please try again.')
        setPaying(false)
      })
      rzp.open()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.')
      setPaying(false)
    }
  }

  // Format amount for display
  const amountStr = info?.amount
    ? `${Number(info.amount).toLocaleString('en-IN')}`
    : null

  // ── Success screen ───────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="card max-w-md w-full text-center">
          <div className="flex justify-center mb-5">
            <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
              <OsteoLogo size="lg" />
            </a>
          </div>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Payment Successful</h2>
          <p className="text-sm text-gray-500 mb-4">
            Your payment of <span className="font-semibold text-gray-700">INR {amountStr}</span> for
            Case <span className="font-mono font-semibold">{caseId}</span> has been received.
          </p>
          {paymentId && (
            <p className="text-xs text-gray-400 mb-4">
              Payment ID: <span className="font-mono">{paymentId}</span>
            </p>
          )}
          <p className="text-xs text-gray-400">
            A confirmation has been sent to your email/phone.
          </p>
          <a
            href="https://www.osteo3d.com"
            className="btn-primary w-full mt-6 inline-block text-center"
          >
            Back to Osteo3D
          </a>
        </div>
      </div>
    )
  }

  // ── Already paid / waived ────────────────────────────────────────
  if (info && (info.status === 'PAID' || info.status === 'WAIVED')) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="card max-w-md w-full text-center">
          <div className="flex justify-center mb-5">
            <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
              <OsteoLogo size="lg" />
            </a>
          </div>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {info.status === 'PAID' ? 'Payment Already Completed' : 'Payment Waived'}
          </h2>
          <p className="text-sm text-gray-500">
            {info.status === 'PAID'
              ? `The payment for Case ${caseId} has already been completed. No further action is required.`
              : `The payment for Case ${caseId} has been waived. No payment is required.`}
          </p>
          <a href="https://www.osteo3d.com" className="btn-primary w-full mt-6 inline-block text-center">
            Back to Osteo3D
          </a>
        </div>
      </div>
    )
  }

  // ── No payment required ──────────────────────────────────────────
  if (info && info.status === 'NONE') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="card max-w-md w-full text-center">
          <div className="flex justify-center mb-5">
            <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
              <OsteoLogo size="lg" />
            </a>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">No Payment Required</h2>
          <p className="text-sm text-gray-500">
            There is no pending payment for Case <span className="font-mono font-semibold">{caseId}</span>.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            If you believe this is an error, please contact your Osteo3D coordinator.
          </p>
          <a href="https://www.osteo3d.com" className="btn-primary w-full mt-6 inline-block text-center">
            Back to Osteo3D
          </a>
        </div>
      </div>
    )
  }

  // ── Main: Case ID entry + payment form ───────────────────────────
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="card max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <a
            href="https://www.osteo3d.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-brand-500 transition-colors mb-4"
          >
            &larr; Back to osteo3d.com
          </a>
          <div className="flex justify-center mb-5">
            <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
              <OsteoLogo size="lg" />
            </a>
          </div>
          <h2 className="text-lg font-semibold text-gray-700">Payment Portal</h2>
          <p className="text-gray-400 text-sm mt-0.5">Secure Online Payment</p>
        </div>

        {/* Error */}
        {error && (
          <p className="mb-4 text-sm text-red-600 flex items-start gap-1.5">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        )}

        {/* Step 1: Case ID lookup (if no info loaded yet) */}
        {!info && !loading && (
          <form onSubmit={handleLookup} noValidate>
            <label htmlFor="caseId" className="block text-sm font-medium text-gray-700 mb-1.5">
              Case ID
            </label>
            <input
              ref={inputRef}
              id="caseId"
              type="text"
              value={input}
              onChange={handleChange}
              placeholder="O3D-K7QM2P9X"
              maxLength={50}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="input font-mono tracking-widest text-center text-lg"
            />
            <button type="submit" className="btn-primary w-full mt-4">
              Look Up Payment
            </button>
            <p className="mt-4 text-center text-xs text-gray-400">
              Enter the Case ID provided by your Osteo3D coordinator.
            </p>
          </form>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center py-8">
            <Spinner />
            <p className="text-sm text-gray-400 mt-3">Looking up payment details…</p>
          </div>
        )}

        {/* Step 2: Payment form */}
        {info && info.status === 'PENDING' && (
          <div className="space-y-5">
            {/* Amount card */}
            <div className="bg-gray-50 rounded-xl px-5 py-4 text-center">
              <p className="text-xs text-gray-400 mb-1">Amount Due</p>
              <p className="text-3xl font-bold text-gray-900">
                <span className="text-lg font-normal text-gray-500">INR </span>
                {amountStr}
              </p>
              <p className="font-mono text-xs text-gray-400 mt-1">Case {caseId}</p>
              {info.description && (
                <p className="text-xs text-gray-500 mt-2">{info.description}</p>
              )}
            </div>

            {/* Payer details */}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                <input
                  type="text"
                  value={payerName}
                  onChange={(e) => { setPayerName(e.target.value); setError(null) }}
                  placeholder="Full name"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={payerEmail}
                  onChange={(e) => { setPayerEmail(e.target.value); setError(null) }}
                  placeholder="you@example.com"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={payerPhone}
                  onChange={(e) => { setPayerPhone(e.target.value); setError(null) }}
                  placeholder="+91 98765 43210"
                  className="input"
                />
              </div>
              <p className="text-xs text-gray-400">
                Provide at least one of email or phone for your payment receipt.
              </p>
            </div>

            {/* Pay button */}
            <button
              onClick={handlePay}
              disabled={paying}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {paying ? (
                <>
                  <Spinner /> Processing…
                </>
              ) : (
                `Pay INR ${amountStr}`
              )}
            </button>

            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secured by Razorpay
            </div>
          </div>
        )}
      </div>

      {/* Admin link */}
      <a
        href="/admin/login"
        className="mt-8 text-xs text-gray-300 hover:text-gray-400 transition-colors"
      >
        Admin portal
      </a>
    </div>
  )
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}
